/**
 * `/scan` — the Rollo scan station, built as a full-bleed, forced-dark, touch-first iPad kiosk (no app
 * shell / sidebar). An operator scans a garment tag; the page resolves it via `GET /scan/resolve` and
 * fills the screen with the exact designs plus a big, glanceable status for every scan.
 *
 * Layout is "one see-and-act TILE per print location": each placement is a single tile = location +
 * method, a large 14×17-proportioned preview, the design name, and — for an actionable DTF placement — a
 * full-width "Mark pressed" bar at its base. The press bar (NOT the art) is the tap target, so a stray
 * touch on the preview can't mint a press. The tiles fill the working area (art as large as possible), a
 * compact status/identity band sits on top, and a thin footer carries the running "X of N pressed →
 * complete" progress plus the rare "Mark defective → reprint". Screen-print placements are shown for
 * reference but pressed at the screen-print worklist.
 *
 * Press-mode (press-mode-view.tsx, per-device switch in the header) replaces the tile grid with the
 * PLATEN drawn at true proportion, the design placed on it by a placement rule, and the heat press's
 * laser guides drawn where the design's edges land — one side at a time, with a pager. The status band,
 * the footer, the handlers and every guard below are shared with the Classic tiles; Press-mode changes
 * only what is drawn around the same press bar (scan-press-bar.tsx).
 *
 * Money path: pressing drives the EXISTING per-copy press route (`POST /batches/:batchId/press` —
 * owed-guarded + settleLine + scope-keyed reverse, PR #194); tapping a pressed side un-presses it (the
 * reversible reverse). A defect drives the EXISTING reissue endpoint. All the handlers + their guards
 * below are unchanged from the prior layout — only presentation changed.
 *
 * Capture is PAGE-LEVEL (`useScannerCapture`): the wedge scanner's keystroke burst + Enter is caught on
 * the document, so the whole screen is the scan target — no captive text field to keep focused. A
 * "Type a code" button opens an on-demand manual field for a torn label (capture pauses while it's up).
 */

import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  Check,
  ImageOff,
  Keyboard,
  Loader2,
  PackagePlus,
  Printer,
  RotateCcw,
  ScanLine,
  X,
} from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useWakeLock } from '@/hooks/use-wake-lock';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import { useAuth } from '@/lib/auth-state';
import { cn } from '@/lib/utils';
import { stripStyleCode } from './design-lib';
import { invalidateInventory } from './inventory-lib';
import { orderCardSearch } from './order-detail-lib';
import {
  AUTO_ADVANCE_DISARM_MS,
  initialSideIndex,
  loadPressPrefs,
  nextUnpressedIndex,
  type PressPrefs,
  pagerOrder,
  savePressPrefs,
  stickyAfterPress,
} from './press-guide-lib';
import { LayoutSwitch, PressModeBody, type PressModeControls } from './press-mode-view';
import { useScannerCapture } from './scan-capture';
import {
  garmentBadge,
  garmentDtfDone,
  isPressable,
  pressBodyFor,
  queueAddBodyFor,
  type ReissueNotOwedReason,
  type ReissueTokenState,
  reissueBodyFor,
  reissueTokenFor,
  reprintDestination,
  type ScanStatusView,
  type ScanTone,
  scanStatusView,
} from './scan-lib';
import { PressBar } from './scan-press-bar';
import { useStyleCodes } from './use-style-codes';

type ScanResult = components['schemas']['ScanResolveResult'];
type ScanPlacement = NonNullable<ScanResult['placements']>[number];

// Forced-dark station, so the tone bands use the dark palette directly (the whole tree is `.dark`).
const BAND_TONE: Record<ScanTone, string> = {
  success: 'border-emerald-800 bg-emerald-950/40',
  warn: 'border-amber-800 bg-amber-950/40',
  error: 'border-red-800 bg-red-950/40',
  info: 'border-border bg-muted/30',
};
const TONE_TEXT: Record<ScanTone, string> = {
  success: 'text-emerald-200',
  warn: 'text-amber-200',
  error: 'text-red-200',
  info: 'text-foreground',
};

/** Best-effort scan feedback tone. Silent if WebAudio is unavailable (the scan/tap is the gesture). */
let audioCtx: AudioContext | undefined;
function beep(kind: 'ok' | 'bad'): void {
  try {
    audioCtx ??= new (
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    )();
    if (audioCtx.state === 'suspended') void audioCtx.resume();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    osc.type = 'square';
    osc.frequency.value = kind === 'ok' ? 880 : 200;
    gain.gain.setValueAtTime(0.06, audioCtx.currentTime);
    osc.start();
    osc.stop(audioCtx.currentTime + (kind === 'ok' ? 0.1 : 0.3));
  } catch {
    // audio unavailable — silent
  }
}

/** Immutably flip a single placement's `pressed` flag (keyed by its press-route location). */
function withPlacementPressed(result: ScanResult, location: string, pressed: boolean): ScanResult {
  return {
    ...result,
    placements: result.placements?.map((p) => (p.location === location ? { ...p, pressed } : p)),
  };
}

/** In-flight key SCOPED to the scanned garment (`code`) + placement location, so an in-flight press on
 *  garment A never disables the same-named side of a different garment B scanned before A settles. */
function pendingKey(code: string, location: string): string {
  return `${code} ${location}`;
}

/** Compact status + identity band. Colour tracks the resolve tone; folds the order/size/batch chips in. */
function StatusBand({
  result,
  view,
  done,
  badge,
}: {
  result: ScanResult;
  view: ScanStatusView;
  done: boolean;
  /** The garment's glanceable badge, already resolved by `garmentBadge` (front-family, live-joined). */
  badge: string;
}) {
  return (
    <div
      className={cn('flex flex-none items-center gap-4 border-b px-5 py-3', BAND_TONE[view.tone])}
    >
      {view.showDesign && (
        <span
          className={cn(
            'flex-none rounded-xl border-2 border-current px-3 py-1.5 font-mono text-3xl font-extrabold leading-none',
            TONE_TEXT[view.tone],
          )}
        >
          {badge}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          {result.orderName && result.storeId && result.orderId ? (
            // Touch-only link to the job card (production-records R0). tabIndex=-1 keeps it OFF the
            // focus order: the scanner's trailing Enter must never activate a navigation mid-scan
            // (the same keystroke-capture hazard the press bar guards against).
            <Link
              to="/orders/$storeId/$orderId"
              params={{ storeId: result.storeId, orderId: result.orderId }}
              search={orderCardSearch('scan')}
              tabIndex={-1}
              className="font-mono text-2xl font-extrabold leading-none underline decoration-muted-foreground/40 underline-offset-4"
              title="Open this order's job card"
            >
              {result.orderName}
            </Link>
          ) : result.orderName ? (
            <span className="font-mono text-2xl font-extrabold leading-none">
              {result.orderName}
            </span>
          ) : null}
          {result.variantTitle && <span className="text-xl font-bold">{result.variantTitle}</span>}
          {result.title && <span className="truncate text-base opacity-80">{result.title}</span>}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          {result.owedRank != null && result.copyOf != null && (
            <span className="rounded-md border px-2 py-0.5">
              copy {result.owedRank} of {result.copyOf}
            </span>
          )}
          {result.batchSeq != null && (
            <span className="rounded-md border px-2 py-0.5">
              Batch #{result.batchSeq}
              {result.reissueOf ? ' (reprint)' : ''}
            </span>
          )}
          {result.batchStatus && (
            <span className="rounded-md border px-2 py-0.5">{result.batchStatus}</span>
          )}
          <span>{done ? 'Every side pressed — set it aside.' : view.hint}</span>
        </div>
      </div>
      <div
        className={cn(
          'flex flex-none items-center gap-2 text-lg font-extrabold',
          TONE_TEXT[view.tone],
        )}
      >
        {done ? (
          <>
            <Check className="size-5" /> Complete
          </>
        ) : (
          view.title
        )}
      </div>
    </div>
  );
}

/**
 * One see-and-act tile for a print location: header (location + method), a big 14×17-proportioned
 * preview (display-only — NOT the tap target), the design name, and a base press bar. A DTF + actionable
 * placement's bar is a real toggle driving the per-copy press; screen-print or a non-actionable side gets
 * a muted informational bar. Pressed → the whole tile goes green. Same `onToggle` (per-copy press) as before.
 */
function PlacementTile({
  p,
  styleCode,
  pressable,
  pending,
  onToggle,
}: {
  p: ScanPlacement;
  /** This placement's design style code (live-joined by designId), shown as a glanceable badge. */
  styleCode?: string;
  pressable: boolean;
  pending: boolean;
  onToggle: () => void;
}) {
  const [broken, setBroken] = useState(false);
  const method = p.method === 'screen-print' ? 'SP' : 'DTF';
  const pressed = p.pressed === true;
  // The design name is a frozen snapshot that may still carry the code; strip it so it isn't shown
  // twice next to the header badge. '—' when the name was only the code (or empty).
  const stripped = stripStyleCode(p.designName ?? '', styleCode);
  const nameText =
    stripped && stripped.toUpperCase() !== (styleCode ?? '').toUpperCase() ? stripped : '—';

  return (
    <div
      className={cn(
        'flex min-h-0 flex-col overflow-hidden rounded-2xl border-2 transition-colors',
        pressed ? 'border-emerald-500 bg-emerald-950/30' : 'border-border bg-card',
      )}
    >
      <div className="flex flex-none items-center justify-between gap-2 px-4 pt-3 pb-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-base font-extrabold uppercase tracking-wide">{p.location}</span>
          {styleCode && (
            <span className="rounded-md border px-1.5 py-0.5 font-mono text-sm font-bold leading-none">
              {styleCode}
            </span>
          )}
        </div>
        <div className="flex flex-none items-center gap-2">
          {/* Pulled off the shelf → this transfer is NOT on the sheet the operator just cut. Amber is the
              established from-stock colour (press worklist, order job card); the run sheet's wording. */}
          {p.fromStock && (
            <span className="rounded border-2 border-amber-500 px-2 py-0.5 text-xs font-extrabold uppercase tracking-wide text-amber-500">
              From stock
            </span>
          )}
          <span
            className={cn(
              'rounded px-2 py-0.5 text-xs font-bold',
              p.method === 'screen-print'
                ? 'bg-muted text-foreground'
                : 'bg-foreground text-background',
            )}
          >
            {method}
          </span>
        </div>
      </div>

      {/* Preview area. The frame owns the leftover height; container-query units size the box to the
          LARGEST 14×17 (the common DTF transfer proportion) that fits — art in true proportion, never a
          long panel. Black bg so light/halftone art on a transparent webp stays visible. Display only. */}
      <div
        className="flex min-h-0 flex-1 items-center justify-center px-4"
        style={{ containerType: 'size' }}
      >
        <div
          className="grid place-items-center overflow-hidden rounded-xl border bg-black"
          style={{ width: 'min(100%, calc(100cqh * 14 / 17))', aspectRatio: '14 / 17' }}
        >
          {p.previewUrl && !broken ? (
            <img
              src={p.previewUrl}
              alt={p.designName ?? p.location}
              loading="lazy"
              className="h-full w-full object-contain"
              onError={() => setBroken(true)}
            />
          ) : (
            <ImageOff className="size-10 text-muted-foreground" />
          )}
        </div>
      </div>

      <div
        className="flex-none truncate px-4 pt-2 pb-2 text-base font-medium"
        title={nameText === '—' ? '' : nameText}
      >
        {nameText}
      </div>

      {/* The bar — not the art — is the tap target (scan-press-bar.tsx, shared with Press-mode). */}
      <PressBar
        method={p.method}
        pressed={pressed}
        pressable={pressable}
        pending={pending}
        onToggle={onToggle}
      />
    </div>
  );
}

/** Footer-left running progress: "X of N pressed" with a bar, flipping to a big "complete" cue. */
function FooterProgress({
  done,
  pressed,
  total,
  hint,
}: {
  done: boolean;
  pressed: number;
  total: number;
  hint: string;
}) {
  if (total <= 0) return <span className="text-sm text-muted-foreground">{hint}</span>;
  if (done) {
    return (
      <span className="flex items-center gap-2 text-lg font-extrabold text-emerald-300">
        <Check className="size-5" /> Garment complete — set it aside, scan the next
      </span>
    );
  }
  return (
    <div className="flex items-center gap-3 text-muted-foreground">
      <span className="text-base font-semibold">
        {pressed} of {total} {total === 1 ? 'side' : 'sides'} pressed
      </span>
      <span className="h-2 w-40 overflow-hidden rounded-full bg-muted">
        <span
          className="block h-full bg-emerald-500 transition-[width]"
          style={{ width: `${(pressed / total) * 100}%` }}
        />
      </span>
    </div>
  );
}

function ScanResultView({
  result,
  styleCodeByDesignId,
  pending,
  onToggle,
  press,
  reprintSlot,
  shelfSlot,
}: {
  result: ScanResult;
  styleCodeByDesignId: ReadonlyMap<string, string>;
  pending: ReadonlySet<string>;
  onToggle: (p: ScanPlacement) => void;
  /** Press-mode controls — present ⇒ the platen frame + rail replace the tile grid (band + footer stay). */
  press?: PressModeControls;
  /** The "Mark defective → reprint" panel, placed in the footer (null when not offered). */
  reprintSlot?: ReactNode;
  /** The "Add to shelf" garment-stock intake panel (finished-garment-stock v1 PR-2) — offered only
   *  for a garment that is DONE with production (ALREADY_PRINTED) or no longer owed (DO_NOT_PRESS),
   *  i.e. exactly the orphan shirts that pile up with no home. Null otherwise. */
  shelfSlot?: ReactNode;
}) {
  const view = scanStatusView(result.status);
  const placements = result.placements ?? [];
  const done = result.status === 'OK' && garmentDtfDone(placements);
  const dtf = placements.filter((p) => p.method === 'dtf');
  const pressableCount = result.status === 'OK' ? dtf.length : 0;
  const pressedCount = dtf.filter((p) => p.pressed).length;
  const codeOf = (p: ScanPlacement) =>
    p.designId ? styleCodeByDesignId.get(p.designId) : undefined;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <StatusBand
        result={result}
        view={view}
        done={done}
        badge={garmentBadge(placements, styleCodeByDesignId)}
      />

      {result.status === 'STOCK_BIN' && result.stockBin ? (
        <StockBinView bin={result.stockBin} />
      ) : view.showDesign && placements.length > 0 ? (
        <>
          {press ? (
            <PressModeBody
              result={result}
              placements={placements}
              styleCodeByDesignId={styleCodeByDesignId}
              isPressable={(p) => isPressable(result, p)}
              isPending={(p) => pending.has(pendingKey(result.code, p.location))}
              onToggle={onToggle}
              controls={press}
            />
          ) : (
            <div
              className={cn(
                'grid min-h-0 flex-1 gap-4 p-4 sm:p-5',
                placements.length === 1 ? 'mx-auto w-full max-w-3xl grid-cols-1' : 'grid-cols-2',
              )}
            >
              {placements.map((p) => (
                <PlacementTile
                  key={`${p.location}-${p.designId}`}
                  p={p}
                  styleCode={codeOf(p)}
                  pressable={isPressable(result, p)}
                  pending={pending.has(pendingKey(result.code, p.location))}
                  onToggle={() => onToggle(p)}
                />
              ))}
            </div>
          )}
          {(pressableCount > 0 || reprintSlot || shelfSlot) && (
            <footer className="flex flex-none flex-wrap items-center justify-between gap-x-6 gap-y-3 border-t px-5 py-3">
              <FooterProgress
                done={done}
                pressed={pressedCount}
                total={pressableCount}
                hint={view.hint}
              />
              {shelfSlot && <div className="flex-none">{shelfSlot}</div>}
              {reprintSlot && <div className="flex-none">{reprintSlot}</div>}
            </footer>
          )}
        </>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
          <div className={cn('text-3xl font-extrabold', TONE_TEXT[view.tone])}>{view.title}</div>
          <div className="max-w-xl text-lg text-muted-foreground">{view.hint}</div>
          {result.status === 'UNRECOGNIZED' && result.code && (
            <code className="mt-1 break-all rounded-md border px-3 py-2 text-sm">
              {result.code}
            </code>
          )}
        </div>
      )}
    </div>
  );
}

type StockBin = NonNullable<ScanResult['stockBin']>;

/**
 * A scanned BIN-label QR (bin-labels plan PR-3, § 3.5) — "what is this stack?", answered standing at
 * the shelf: the identity exactly as the printed label reads it (the server composes both from the
 * same ledger row), live on-hand, art thumbnails, and an inline COUNT that anchors on-hand to what's
 * physically in the bag (`POST /garment-stock/count` — an anchor movement, not a money path).
 *
 * Count is ref-latched with one idempotency token per submit context, the AddToShelfPanel pattern: a
 * double-tap can't outrun the re-render, and a retry of a failed save dedupes server-side.
 */
function StockBinView({ bin }: { bin: StockBin }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [onHand, setOnHand] = useState(bin.onHand);
  const [counted, setCounted] = useState('');
  const [state, setState] = useState<
    | { phase: 'idle' }
    | { phase: 'saving' }
    | { phase: 'saved'; at: number }
    | { phase: 'error'; detail: string }
  >({ phase: 'idle' });
  const busyRef = useRef(false);
  const tokenRef = useRef<string>(crypto.randomUUID());

  const n = Number(counted.trim());
  const validCount = counted.trim() !== '' && Number.isInteger(n) && n >= 0;

  const saveCount = async () => {
    if (busyRef.current || !validCount) return;
    busyRef.current = true;
    setState({ phase: 'saving' });
    try {
      const { data, error } = await client.POST('/garment-stock/count', {
        body: { stockKey: bin.stockKey, countedQty: n, clientToken: tokenRef.current },
      });
      if (error || !data?.ok) {
        setState({ phase: 'error', detail: error?.error?.detail ?? 'Could not save the count.' });
        return;
      }
      invalidateInventory(qc);
      setOnHand(data.data.onHand);
      setCounted('');
      tokenRef.current = crypto.randomUUID(); // the next count is a new action
      setState({ phase: 'saved', at: n });
    } finally {
      busyRef.current = false;
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5 p-6">
      <div className="flex items-center gap-6">
        <div className="text-center">
          <div className={cn('text-6xl font-extrabold tabular-nums', onHand < 0 && 'text-red-400')}>
            {onHand}
          </div>
          <div className="text-sm text-muted-foreground">on hand</div>
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md border-2 border-foreground px-2 py-0.5 text-2xl font-extrabold">
              {bin.sizeName || '?'}
            </span>
            {bin.apparelKind && (
              <span className="rounded border px-1.5 py-0.5 text-sm font-bold">
                {bin.apparelKind === 'hoodie' ? 'HOODIE' : 'TEE'}
              </span>
            )}
          </div>
          <div className="mt-1 text-lg font-medium">{bin.blankLabel}</div>
          <div className="text-xs text-muted-foreground">
            bin <span className="font-mono">{bin.binId}</span>
            {bin.lastCountedAt
              ? ` · last counted ${new Date(bin.lastCountedAt).toLocaleDateString()}`
              : ' · never counted'}
          </div>
        </div>
      </div>

      {bin.placements.length > 0 && (
        <div className="flex flex-wrap items-start justify-center gap-3">
          {bin.placements.map((p) => (
            <div key={p.location} className="w-28 text-center">
              <div className="h-32 w-28 overflow-hidden rounded-md border bg-zinc-900">
                {p.previewUrl ? (
                  <img src={p.previewUrl} alt="" className="size-full object-contain" />
                ) : (
                  <div className="flex size-full items-center justify-center text-xs text-muted-foreground">
                    no preview
                  </div>
                )}
              </div>
              <div className="mt-1 truncate text-xs">
                <span className="font-bold uppercase">{p.location}</span>
                {p.designName ? ` ${p.designName}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <div>
          <div className="mb-1 text-xs text-muted-foreground">Count the bag (anchors on-hand)</div>
          <Input
            type="number"
            inputMode="numeric"
            min="0"
            step="1"
            className="h-12 w-28 text-lg"
            value={counted}
            onChange={(e) => setCounted(e.target.value)}
            aria-label="Counted quantity"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          className="min-h-[48px]"
          disabled={!validCount || state.phase === 'saving'}
          onClick={saveCount}
        >
          {state.phase === 'saving' ? <Loader2 className="size-5 animate-spin" /> : 'Save count'}
        </Button>
      </div>
      {state.phase === 'saved' && (
        <p className="text-sm text-emerald-400">
          <Check className="mr-1 inline size-4" />
          Anchored at {state.at}.
        </p>
      )}
      {state.phase === 'error' && <p className="text-sm text-red-400">{state.detail}</p>}
    </div>
  );
}

/**
 * The garment's row in the shared reprint queue, as this station last saw it.
 *
 * `merged` = the add landed on a row ANOTHER station had already created. `rev` is the row's revision
 * AS SEEN by that add — both the Undo and "Print now" re-use it, because a later report by another
 * station moves `rev` while leaving `entryId` (and therefore the server's delete condition) alone.
 */
type QueuedRow = { entryId: string; merged: boolean; transferOnly: boolean; rev: number };

/** What "Print now" is about to spend, as quoted BY THE SERVER — never computed here. */
type FlushCost = { garments: number; replacementBlanks: number; sheets: number };

/**
 * Defect→reprint flow state. A deliberate button → confirm → a server write → a terminal state
 * carrying whatever the reverse needs. Reset on every new scan so it never bleeds onto the next
 * garment.
 *
 * The queued row has FIVE resting states rather than one, because "Print now" (P6b, owner decision
 * D4) is a two-step spend and each step has to be distinguishable from the others on a glance:
 *  · `queued`      — in the queue, nothing printed. The normal end of a defect report.
 *  · `quoting`     — asking the server what a sheet would cost.
 *  · `quoted`      — the server's cost is on screen, awaiting the deliberate Confirm.
 *  · `printing`    — the confirmed flush is in flight.
 *  · `flushFailed` — the flush was refused. The row is untouched; the copy must not imply otherwise,
 *                    and must never read as "printed".
 * Plus the two terminal shapes:
 *  · `done`  — a sheet EXISTS. `fromQueue` distinguishes a flushed queue row (discarding the sheet
 *              sends the garment BACK to the queue, still needing a reprint) from the not-owed
 *              immediate reissue (discarding it drops the reprint entirely).
 *  · `undoing` — that discard is in flight.
 */
type ReprintState =
  | { phase: 'idle' }
  | { phase: 'confirming' }
  | { phase: 'pending' }
  | ({ phase: 'queued' } & QueuedRow)
  | ({ phase: 'removing' } & QueuedRow)
  | ({ phase: 'quoting' } & QueuedRow)
  | ({ phase: 'quoted'; cost: FlushCost } & QueuedRow)
  | ({ phase: 'printing' } & QueuedRow)
  | ({ phase: 'flushFailed'; problem: string } & QueuedRow)
  /** The flush CONVERGED: the garment was already on a sheet somebody else built. Informational and
   *  deliberately non-destructive — see `printNow`. */
  | ({ phase: 'onSheet'; seq?: number } & QueuedRow)
  /** `row` is carried only for a flushed QUEUE row, so discarding the sheet can land back on the
   *  queued panel truthfully: `unflush` clears the flush marks WITHOUT bumping `rev`, so the revision
   *  this station pinned is still the live one. */
  | { phase: 'done'; batchId: string; seq?: number; fromQueue?: true; row?: QueuedRow }
  | { phase: 'undoing'; batchId: string; seq?: number; fromQueue?: true; row?: QueuedRow };

/** The Add-to-shelf panel's resting states (finished-garment-stock v1 PR-2). */
type ShelfState =
  | { phase: 'idle' }
  | { phase: 'pending' }
  | { phase: 'added'; onHand: number; duplicateCode: boolean }
  | { phase: 'error'; detail: string };

/**
 * "Add to shelf" — the garment-stock SCAN intake door, sized for the footer. One tap = +1 of this
 * exact garment (frozen batch-item identity, resolved server-side from the scanned code). Offered
 * only for ALREADY_PRINTED / DO_NOT_PRESS garments (see the shelfSlot wiring). Guarded by a REF, not
 * `disabled={isPending}` — a ref is checked synchronously, so a double-tap can't outrun the
 * re-render and add two. A repeated code comes back as an ADVISORY flag, never a refusal (a
 * re-batched line legitimately reuses codes) — the amber note tells the operator what to check.
 */
function AddToShelfPanel({ code }: { code: string }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [state, setState] = useState<ShelfState>({ phase: 'idle' });
  const busyRef = useRef(false);
  // One idempotency token per ADD: a retry of a failed tap reuses it (the server dedupes); a
  // deliberate "Add another" mints a fresh one.
  const tokenRef = useRef<string>(crypto.randomUUID());

  const add = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setState({ phase: 'pending' });
    try {
      const { data, error } = await client.POST('/garment-stock/intake', {
        body: { code, quantity: 1, clientToken: tokenRef.current },
      });
      if (error || !data?.ok) {
        setState({
          phase: 'error',
          detail: error?.error?.detail ?? 'Could not add this shirt to the shelf.',
        });
        return;
      }
      invalidateInventory(qc);
      setState({
        phase: 'added',
        onHand: data.data.onHand.onHand,
        duplicateCode: data.data.duplicateCode === true,
      });
    } finally {
      busyRef.current = false;
    }
  };

  if (state.phase === 'added') {
    return (
      <div className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-3">
          <span className="text-sm text-emerald-400">
            <Check className="mr-1 inline size-4" />
            On the shelf — now {state.onHand}
          </span>
          <Button
            type="button"
            variant="ghost"
            className="min-h-[56px] text-base text-muted-foreground"
            onClick={() => {
              tokenRef.current = crypto.randomUUID();
              setState({ phase: 'idle' });
            }}
          >
            Add another
          </Button>
        </div>
        {state.duplicateCode && (
          <p className="max-w-xs text-right text-xs text-amber-300">
            This tag was added before — if it's the same shirt, remove one via Adjust on the
            Inventory → Printed garments tab.
          </p>
        )}
        {/* This kiosk can't reach a thermal printer (the print-queue rationale), so point at the
            surface that can rather than offer a button that opens a printerless dialog. */}
        <p className="max-w-xs text-right text-xs text-muted-foreground">
          New stack? Print its bin label from Inventory → Printed garments.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        type="button"
        variant="outline"
        onClick={add}
        disabled={state.phase === 'pending'}
        className="min-h-[56px] text-base text-sky-300"
      >
        {state.phase === 'pending' ? (
          <Loader2 className="size-5 animate-spin" />
        ) : (
          <PackagePlus className="size-5" />
        )}
        Add to shelf
      </Button>
      {state.phase === 'error' && (
        <p className="max-w-xs text-right text-xs text-red-400">{state.detail}</p>
      )}
    </div>
  );
}

/**
 * The garment-level "Mark defective" control (only rendered when `reprintDestination` isn't
 * `hidden`), sized for the footer. Confirm-gated so a stray scan can't report a garment nobody
 * touched. DTF-only, whole-garment (owner decisions).
 *
 * Two modes, because a defect has two destinations (see `reprintDestination`):
 *  · `queue`   — the normal path. Confirming asks the one question the press can answer — did the
 *                blank go with it? — and the answer IS the confirmation, so there is no separate
 *                "are you sure". Nothing prints; the garment joins the shared queue.
 *  · `reissue` — a garment nobody owes. Unchanged: a reason, then its own sheet immediately.
 */
function ReprintPanel({
  state,
  note,
  crossRunNote,
  mode,
  onStart,
  onConfirmQueue,
  onConfirmReissue,
  onCancel,
  onRemove,
  onPrintNow,
  onUndo,
}: {
  state: ReprintState;
  note: string | null;
  /** The cross-run advisory, if the server found other runs of this line queued. Amber, never a refusal. */
  crossRunNote: string | null;
  mode: 'queue' | 'reissue';
  onStart: () => void;
  /** `blankRuined` — the press's one question. See `queueAddBodyFor` for why it is never defaulted. */
  onConfirmQueue: (blankRuined: boolean) => void;
  onConfirmReissue: (reason: ReissueNotOwedReason) => void;
  onCancel: () => void;
  onRemove: () => void;
  /** Ask the server what a one-garment sheet costs (`confirmed: false`), then spend it (`true`). */
  onPrintNow: (confirmed: boolean) => void;
  onUndo: () => void;
}) {
  const notOwed = mode === 'reissue';
  return (
    <div className="flex flex-col items-end gap-1">
      {state.phase === 'idle' && (
        <Button
          type="button"
          variant="outline"
          onClick={onStart}
          className={cn('min-h-[56px] text-base', notOwed ? 'text-amber-300' : 'text-red-400')}
        >
          <AlertTriangle className="size-5" />{' '}
          {notOwed ? 'Not owed — reprint anyway?' : 'Mark defective'}
        </Button>
      )}
      {/* The blank question. Two buttons rather than a toggle-then-confirm: the operator cannot
          submit without answering, and there is no default to get wrong. Sending "blank is fine"
          when it is not draws ZERO replacement blanks for the line and leaves the presser with a
          transfer and no shirt; the opposite mistake costs one spare blank. So the ruined answer is
          the prominent one, and both are one tap. */}
      {state.phase === 'confirming' && !notOwed && (
        <div className="flex flex-col items-end gap-2 rounded-md border border-red-500/40 bg-red-950/30 p-3">
          <span className="text-sm text-red-100">Blank ruined too?</span>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              onClick={() => onConfirmQueue(true)}
              className="min-h-[56px] bg-red-600 text-base text-white hover:bg-red-700"
            >
              Yes — pull a fresh blank
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onConfirmQueue(false)}
              className="min-h-[56px] text-base"
            >
              No — transfer only
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              className="min-h-[56px] text-base"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      {/* Nobody owes this garment, so the reprint spends a blank on work no customer is paying for.
          The reason is REQUIRED and comes from fixed buttons — never a text input, which would swallow
          the next scan (page-level capture skips keydowns while an input is focused). */}
      {state.phase === 'confirming' && notOwed && (
        <div className="flex flex-col items-end gap-2 rounded-md border border-amber-500/40 bg-amber-950/30 p-3">
          <span className="text-sm text-amber-200">
            Nobody owes this garment — it was refunded or cancelled. Reprinting still pulls a blank.
            Why?
          </span>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {(
              [
                ['goodwill', 'Goodwill'],
                ['customer-confirmed', 'Customer confirmed'],
                ['staff-error', 'Staff error'],
              ] as const
            ).map(([reason, label]) => (
              <Button
                key={reason}
                type="button"
                onClick={() => onConfirmReissue(reason)}
                className="min-h-[56px] bg-amber-600 text-base text-white hover:bg-amber-700"
              >
                {label}
              </Button>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="min-h-[56px] text-base"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      {state.phase === 'pending' && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />{' '}
          {notOwed ? 'Queuing reprint…' : 'Adding to the reprint queue…'}
        </div>
      )}
      {(state.phase === 'quoting' || state.phase === 'printing') && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />{' '}
          {state.phase === 'quoting' ? 'Checking what a sheet costs…' : 'Printing…'}
        </div>
      )}
      {/* Queued: nothing has printed, and saying so is the whole point — the operator must not walk
          away expecting a sheet. The Undo is offered ONLY when this report CREATED the row. On a
          merge the row is another station's too, and removing it would delete their defect report
          along with this one; the queue page's reasoned dismiss is the right place for that. */}
      {/* The server's quote, rendered as a CONFIRM rather than an error — it arrives as a 422, but it
          is a price, not a failure. D3: friction on the spend is free; friction on the report is what
          kills the feature. The numbers are the server's own: this station never computes them,
          because the authoritative ones know the live roll, the per-sheet cap and the store split. */}
      {state.phase === 'quoted' && (
        <div className="flex flex-col items-end gap-2 rounded-md border border-amber-500/40 bg-amber-950/30 p-3">
          <span className="text-sm text-amber-100">
            Print this one garment on its own film now?{' '}
            <span className="font-medium">
              {state.cost.garments} garment{state.cost.garments === 1 ? '' : 's'} ·{' '}
              {state.cost.replacementBlanks} replacement blank
              {state.cost.replacementBlanks === 1 ? '' : 's'} · {state.cost.sheets} sheet
              {state.cost.sheets === 1 ? '' : 's'} of film.
            </span>{' '}
            <span className="text-muted-foreground">
              Waiting costs nothing — it would share a film with the day's other reprints.
            </span>
          </span>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              onClick={() => onPrintNow(true)}
              className="min-h-[56px] bg-amber-600 text-base text-white hover:bg-amber-700"
            >
              Print it now
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="min-h-[56px] text-base"
            >
              Keep it in the queue
            </Button>
          </div>
        </div>
      )}
      {/* Refused. The row is untouched, so this must not read as "printed" and must not read as
          "lost" either — it renders the server's own sentence and leaves the queue link in reach. */}
      {state.phase === 'flushFailed' && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          <span className="max-w-xl text-sm font-medium text-red-300">
            Not printed — {state.problem}
          </span>
          <Link
            to="/reprint-queue"
            tabIndex={-1}
            className="inline-flex min-h-[56px] items-center rounded-md border px-4 text-base text-muted-foreground hover:text-foreground"
          >
            Open the reprint queue
          </Link>
          <Button
            type="button"
            variant="outline"
            onClick={() => onPrintNow(false)}
            className="min-h-[56px] text-base"
          >
            Try again
          </Button>
          {/* Without this the failure is a dead end: the only ways out would be retrying, walking to
              the queue page, or rescanning — and a rescan re-arms "Mark defective" over a garment
              that is already queued. This returns to the queued panel, Undo and all. */}
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            className="min-h-[56px] text-base"
          >
            Leave it in the queue
          </Button>
        </div>
      )}
      {/* Converged: the garment is on a sheet somebody else built, and this tap printed nothing. No
          destructive control here on purpose — that sheet is not ours to discard, and it may be
          carrying a dozen other orders' garments. */}
      {state.phase === 'onSheet' && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          <span className="text-sm font-medium text-amber-300">
            ✓ Already on a reprint sheet{state.seq ? ` — Sheet #${state.seq}` : ''}.{' '}
            <span className="font-normal text-muted-foreground">
              Another station printed it with the day's other reprints. Nothing new was printed
              here.
            </span>
          </span>
          <Link
            to="/reprint-queue"
            tabIndex={-1}
            className="inline-flex min-h-[56px] items-center rounded-md border px-4 text-base text-muted-foreground hover:text-foreground"
          >
            Open the reprint queue
          </Link>
        </div>
      )}
      {(state.phase === 'queued' || state.phase === 'removing') && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          <span className="text-sm font-medium text-amber-300">
            {state.merged
              ? '✓ Already reported by another station — your report was merged.'
              : '✓ Added to the reprint queue.'}{' '}
            <span className="font-normal text-muted-foreground">
              {state.transferOnly ? 'Keeping the blank.' : 'A fresh blank will be pulled.'} Nothing
              printed yet — it prints with the day's other reprints.
            </span>
          </span>
          {crossRunNote && (
            <span className="w-full text-sm font-medium text-amber-400">⚠️ {crossRunNote}</span>
          )}
          {/* D4's other half. Deliberately the LEAST prominent control here: one garment on its own
              film is the waste the queue exists to end, so it is available but never the default. */}
          <Button
            type="button"
            variant="ghost"
            onClick={() => onPrintNow(false)}
            disabled={state.phase === 'removing'}
            className="min-h-[56px] text-base text-muted-foreground"
          >
            <Printer className="size-5" /> Print now
          </Button>
          {/* Touch-only, like the identity band's order link: `tabIndex={-1}` keeps it OFF the focus
              order so the scanner's trailing Enter can never navigate away mid-scan — which would
              take the Undo with it. */}
          <Link
            to="/reprint-queue"
            tabIndex={-1}
            className="inline-flex min-h-[56px] items-center rounded-md border px-4 text-base text-muted-foreground hover:text-foreground"
          >
            Open the reprint queue
          </Link>
          {!state.merged && (
            <Button
              type="button"
              variant="outline"
              onClick={onRemove}
              disabled={state.phase === 'removing'}
              className="min-h-[56px] text-base"
            >
              {state.phase === 'removing' ? (
                <Loader2 className="size-5 animate-spin" />
              ) : (
                <>
                  <RotateCcw className="size-5" /> Undo
                </>
              )}
            </Button>
          )}
        </div>
      )}
      {(state.phase === 'done' || state.phase === 'undoing') && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          <span className="text-sm font-medium text-amber-300">
            {state.fromQueue ? '✓ Printing' : '✓ Reprint queued'}
            {state.seq ? ` — Sheet #${state.seq}` : ''}. Press it from the Gang sheets page.
            {/* The two discards mean different things and the operator has to know which one this is:
                a flushed queue row goes BACK to the queue still needing a reprint, whereas the
                not-owed reissue is simply dropped. */}
            {state.fromQueue && (
              <span className="font-normal text-muted-foreground">
                {' '}
                Discarding the sheet puts the garment back in the reprint queue.
              </span>
            )}
          </span>
          <Button
            type="button"
            variant="outline"
            onClick={onUndo}
            disabled={state.phase === 'undoing'}
            className="min-h-[56px] text-base"
          >
            {state.phase === 'undoing' ? (
              <Loader2 className="size-5 animate-spin" />
            ) : (
              <>
                <RotateCcw className="size-5" /> Undo
              </>
            )}
          </Button>
        </div>
      )}
      {note && <p className="text-sm text-muted-foreground">{note}</p>}
    </div>
  );
}

export function ScanStationPage() {
  const client = useApiClient();
  // The kiosk drives its own display imperatively (setResult), deliberately NOT through the query
  // cache — re-scanning IS the refresh, and a cached read behind a money button is a hazard. But its
  // writes still move state every OTHER screen reads, and this page used to invalidate nothing at
  // all, so a full shift at the scanner left /press, /production, /orders and the inventory hub
  // stale until someone hard-reloaded. These helpers touch ONLY other screens' caches; they never
  // read from or write to this page's own state, so there is no reconciliation risk here.
  const qc = useQueryClient();
  /** A garment's pressed state changed → worklists, the batch, its order, and the board all moved. */
  function refreshAfterPress(storeId: string, orderId: string, batchId: string) {
    qc.invalidateQueries({ queryKey: ['press-worklist'] });
    qc.invalidateQueries({ queryKey: ['batches'] });
    qc.invalidateQueries({ queryKey: ['batch', batchId] });
    qc.invalidateQueries({ queryKey: ['order', storeId, orderId] });
    qc.invalidateQueries({ queryKey: ['production-board'] });
    // Pressing the last placement settles the garment, and settleLine CONSUMES its blank.
    invalidateInventory(qc);
  }
  /** A reissue batch appeared or was discarded. Verified NOT to touch inventory: the reissue handler
   *  makes no blank/transfer write (the blank is drawn at consume time), which is also why Undo can
   *  delete a not-yet-printed reissue without reversing anything. So no inventory bust here. */
  function refreshAfterBatchChange(storeId?: string, orderId?: string) {
    qc.invalidateQueries({ queryKey: ['batches'] });
    qc.invalidateQueries({ queryKey: ['production-board'] });
    qc.invalidateQueries({ queryKey: ['orders'] });
    if (storeId && orderId)
      qc.invalidateQueries({ queryKey: ['order-reprints', storeId, orderId] });
  }
  /**
   * A reprint-queue row appeared or was dismissed.
   *
   * ⚠️ Deliberately NOT `['batches']` — a queue add writes no batch row, so that is the one key that
   * did not move. What moved is the queue itself, the order's outstanding-reprint lookup (the ship
   * gate's second leg) and the board. Copying the reissue path's invalidation set here would leave
   * the acting device showing no evidence the garment is queued, and the plausible operator recovery
   * from that is the immediate-reprint button — two films and two blanks for one shirt.
   */
  function refreshAfterQueueChange(storeId: string, orderId: string) {
    qc.invalidateQueries({ queryKey: ['reprint-queue'] });
    qc.invalidateQueries({ queryKey: ['order-reprints', storeId, orderId] });
    qc.invalidateQueries({ queryKey: ['order', storeId, orderId] });
    qc.invalidateQueries({ queryKey: ['production-board'] });
    qc.invalidateQueries({ queryKey: ['orders'] });
  }
  const { state: authState } = useAuth();
  const stationEmail = authState.status === 'signed-in' ? authState.email : undefined;
  const [result, setResult] = useState<ScanResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Manual-entry fallback for a torn/unreadable label. While open, page-level scan capture is paused.
  const [manualOpen, setManualOpen] = useState(false);
  const manualRef = useRef<HTMLInputElement>(null);
  // (code, location) pairs with a press POST in flight — disables just that card (others stay tappable).
  const [pending, setPending] = useState<ReadonlySet<string>>(() => new Set());
  // Defect→reprint flow (Phase 5), scoped to the current scan — reset on every new scan.
  const [reprint, setReprint] = useState<ReprintState>({ phase: 'idle' });
  const [reprintNote, setReprintNote] = useState<string | null>(null);
  /**
   * "This line already has garments queued off another production run" — the server's advisory from the
   * add response.
   *
   * ⚠️ Its OWN state, not `reprintNote` and not a field on the queued row. `reprintNote` is cleared at the
   * start of Undo and of Print now — the two taps this warning exists to make the operator think about —
   * and `QueuedRow` is re-built field by field at four sites, so a field added there vanishes on the first
   * tap. This is cleared only when a NEW GARMENT is scanned, which is exactly the lifetime it should have.
   */
  const [reprintCrossRun, setReprintCrossRun] = useState<string | null>(null);
  // Latest displayed result, readable in async callbacks — used to gate feedback (banner/beep) to the
  // garment the operator is CURRENTLY looking at, so a late press outcome for a previous scan can't paint
  // an error or sound a beep over a different garment scanned in the meantime.
  const resultRef = useRef<ScanResult | null>(result);
  resultRef.current = result;
  /** True iff the operator is still viewing the garment `code` was captured for. */
  const isCurrent = (code: string) => resultRef.current?.code === code;
  // A reissue POST is in flight. A ref (not state) so it SURVIVES the reprint-panel reset that a new
  // scan triggers — otherwise rescanning the same garment mid-POST would re-arm the button and a second
  // Confirm would mint a DUPLICATE billable reprint. Blocks a second mint until the first POST settles.
  const reissuingRef = useRef(false);
  // Idempotency token for the reprint POST, keyed to the garment on screen. A retry of the SAME garment
  // reuses it so the server dedupes instead of minting a second billable reprint; a different garment
  // mints a fresh one. Cleared on success (see confirmReprint) so a genuinely twice-ruined garment can
  // be reprinted twice. A ref, not state, so it survives the panel reset a rescan triggers.
  const reissueTokenRef = useRef<ReissueTokenState>({ token: null, code: null });
  // Monotonic scan id → latest-wins guard. With page-level capture two scans can be in flight at once;
  // drop an out-of-order (stale) resolve so the display always reflects the MOST RECENT scan (every
  // press/reissue is built from the displayed result). Not a money-invariant fix — presses stay
  // owed-gated + tap-gated — it stops a lagging resolve from showing garment A after B was scanned.
  const scanSeqRef = useRef(0);

  // ── Press-mode (display-only) ──────────────────────────────────────────────────────────────────
  // Per-device prefs (layout, guides, preset, platen, collar edge): loaded once, saved on every change.
  const [prefs, setPrefsState] = useState<PressPrefs>(() => loadPressPrefs());
  const setPrefs = (next: PressPrefs) => {
    setPrefsState(next);
    savePressPrefs(next);
  };
  // Which side of the scanned garment is on screen (an index into the pager order).
  const [sideIndex, setSideIndex] = useState(0);
  // The side the NEXT scan should open on — set by a press or a deliberate pager tap, never by an
  // auto-advance (`stickyAfterPress` carries the rule). A ref: it must survive renders without causing one.
  const stickyLocationRef = useRef<string | null>(null);
  // The platen picker is open. Its number inputs would keep the scanner's keystrokes anyway (capture
  // skips editable targets), and the page pauses capture too — belt and braces, like the manual-entry bar.
  const [pickerOpen, setPickerOpen] = useState(false);
  // After an auto-advance the press bar refuses a tap for a moment (D6): the bar sits in the same place on
  // every side, so a double-tap habit would otherwise press the side that just slid under the finger.
  const [barDisarmed, setBarDisarmed] = useState(false);
  const disarmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (disarmTimerRef.current) clearTimeout(disarmTimerRef.current);
    },
    [],
  );
  function disarmBar() {
    setBarDisarmed(true);
    if (disarmTimerRef.current) clearTimeout(disarmTimerRef.current);
    disarmTimerRef.current = setTimeout(() => setBarDisarmed(false), AUTO_ADVANCE_DISARM_MS);
  }
  const orderedPlacements = useMemo(() => pagerOrder(result?.placements ?? []), [result]);

  // Keep the kiosk iPad awake; best-effort no-op where the Wake Lock API is unavailable.
  useWakeLock();
  // designId → styleCode, for the glanceable code badge on each tile + the identity band (display-only).
  const styleCodeByDesignId = useStyleCodes();

  async function resolve(code: string): Promise<ScanResult> {
    const { data, error } = await client.GET('/scan/resolve', { params: { query: { code } } });
    if (error || !data?.ok) throw new Error('Could not reach the scan resolver.');
    return data.data;
  }

  async function submit(code: string) {
    const trimmed = code.trim();
    if (!trimmed) return;
    const seq = ++scanSeqRef.current;
    setLoading(true);
    setErrorMsg(null);
    // A new garment → drop any prior garment's reprint panel state so it can't bleed across scans.
    setReprint({ phase: 'idle' });
    setReprintNote(null);
    setReprintCrossRun(null);
    try {
      const data = await resolve(trimmed);
      if (seq !== scanSeqRef.current) return; // superseded by a newer scan — drop this stale result
      setResult(data);
      // Open on the side the run is pressing (sticky), else the first side still owed a press.
      setSideIndex(initialSideIndex(pagerOrder(data.placements ?? []), stickyLocationRef.current));
      // A bin-label scan resolving is a SUCCESS (the "what is this stack?" answer), not a defect.
      beep(data.status === 'OK' || data.status === 'STOCK_BIN' ? 'ok' : 'bad');
    } catch (e) {
      if (seq !== scanSeqRef.current) return; // stale failure from a superseded scan — ignore
      setResult(null);
      setErrorMsg(e instanceof Error ? e.message : 'Scan failed.');
      beep('bad');
    } finally {
      if (seq === scanSeqRef.current) setLoading(false);
    }
  }

  // Page-level capture: the wedge scanner's keystroke burst + Enter is caught on the document. Paused
  // while the manual-entry field is open so typing there isn't also read as a scan.
  useScannerCapture({ onScan: submit, enabled: !manualOpen && !pickerOpen });

  function submitManual() {
    const v = manualRef.current?.value.trim() ?? '';
    setManualOpen(false);
    if (v) void submit(v);
  }

  // Toggle one DTF placement of the scanned garment via the existing per-copy press route. The optimistic
  // flip is authoritative for the tapped side (the server's 200/422/409 is the source of truth for the
  // WRITE), so there is no follow-up re-read to race or clobber a concurrent tap — a fresh scan re-resolves.
  // A refused copy (422 copy_not_owed) or any failure reverts THIS side. All feedback is gated on the
  // operator still viewing this garment (`isCurrent`), so a late outcome can't bleed onto a later scan.
  async function toggle(p: ScanPlacement) {
    const key = result && pendingKey(result.code, p.location);
    if (!result?.batchId || !isPressable(result, p) || (key && pending.has(key))) return;
    const body = pressBodyFor(result, p, !p.pressed);
    if (!body || !key) return;
    const { batchId, code } = { batchId: result.batchId, code: result.code };
    const next = !p.pressed;
    setResult((prev) =>
      prev && prev.code === code ? withPlacementPressed(prev, p.location, next) : prev,
    );
    setPending((s) => new Set(s).add(key));
    setErrorMsg(null);
    try {
      const res = await client.POST('/batches/{batchId}/press', {
        params: { path: { batchId } },
        body,
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Could not update this garment.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      // Refresh the OTHER screens unconditionally — not gated on `isCurrent`. The write landed
      // regardless of whether the operator has since scanned on, and /press or /production being
      // wrong is exactly how two people press the same garment.
      refreshAfterPress(body.storeId, body.orderId, batchId);
      if (isCurrent(code)) {
        beep('ok');
        // Press-mode: remember the side for the next scan (a run sticks to it, a completed garment clears
        // it), then slide to the next side still owed a press — with the bar disarmed for a moment so the
        // tap that just landed cannot repeat onto it. Display state only: the write above is done and its
        // guards are untouched. `resultRef` already carries the optimistic flip (React re-rendered during
        // the await), so the pager order and the "what is still unpressed" question read the new state.
        const after = pagerOrder(resultRef.current?.placements ?? []);
        stickyLocationRef.current = stickyAfterPress(after, p.location);
        if (prefs.layout === 'press') {
          const from = after.findIndex((x) => x.location === p.location);
          const nextIdx = nextUnpressedIndex(after, from);
          if (nextIdx != null) {
            setSideIndex(nextIdx);
            disarmBar();
          }
        }
      }
    } catch (e) {
      // Revert this side (guarded to the same garment) and surface the failure only if still viewing it.
      setResult((prev) =>
        prev && prev.code === code ? withPlacementPressed(prev, p.location, !next) : prev,
      );
      if (isCurrent(code)) {
        setErrorMsg(e instanceof Error ? e.message : 'Update failed.');
        beep('bad');
      }
    } finally {
      setPending((s) => {
        const n = new Set(s);
        n.delete(key);
        return n;
      });
    }
  }

  // ── Defect → the shared reprint queue (P6) ──────────────────────────────────────────────────────
  // The normal path. One POST, no film, no blank: the garment becomes a row that blocks its order
  // from the ship lanes and waits to merge onto ONE sheet with the cutter's and the office's
  // reprints. `blankRuined` is the operator's answer to the panel's one question.
  //
  // ⚠️ `reissuingRef` and the confirm step are NOT redundant here even though the add is keyed on the
  // garment's identity. The add is monotone, not idempotent: it bumps `rev` unconditionally and never
  // looks at the flush marks, so a second add on a row that has already been flushed onto a sheet
  // moves `rev` past `flushedAtRev`, the retire at mark-printed is refused, and the row is un-flushed
  // back into the queue — a second sheet and a second blank for a garment already reprinted.
  async function confirmQueueAdd(blankRuined: boolean) {
    if (reissuingRef.current) return;
    if (!result) return;
    const body = queueAddBodyFor(result, { blankRuined });
    if (!body) return;
    const code = result.code;
    reissuingRef.current = true;
    setReprint({ phase: 'pending' });
    setReprintNote(null);
    try {
      const res = await client.POST('/batches/reprint-cart/entries', { body });
      // Render the server's own sentence verbatim — the D7 screen-print refusal's "Re-screen it
      // off-system instead" is the only copy that tells the operator what to actually do.
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Could not queue the reprint.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      const { entry, merged, alreadyOutstanding } = res.data.data;
      // The row exists whether or not the operator is still on this garment, so the other screens
      // learn about it either way.
      refreshAfterQueueChange(body.storeId, body.orderId);
      if (isCurrent(code)) {
        setReprint({
          phase: 'queued',
          entryId: entry.entryId,
          rev: entry.rev,
          merged,
          // From the SERVER's row, not from what we sent: on a merge the stored value is whatever the
          // one-way downgrade settled on, and telling the operator "keeping the blank" when another
          // station already reported it scorched would be a lie about a blank.
          transferOnly: entry.transferOnly === true,
        });
        // ⚠️ Describes the LINE, never a specific shirt. Ranks restart when a line is re-batched, so no
        // comparison across runs identifies a garment — which is why this warns rather than refuses.
        const others = alreadyOutstanding ?? [];
        const sheets = [...new Set(others.map((o) => o.sourceBatchSeq).filter(Boolean))];
        setReprintCrossRun(
          others.length === 0
            ? null
            : `This order line already has ${others.length} garment${others.length === 1 ? '' : 's'} waiting to be reprinted from ${
                sheets.length > 0
                  ? `another run (sheet ${sheets.map((n) => `#${n}`).join(', ')})`
                  : 'another production run'
              }. If that is the same shirt, reporting it again builds a second sheet — check before printing.`,
        );
        beep('ok');
      }
    } catch (e) {
      if (isCurrent(code)) {
        setReprint({ phase: 'idle' });
        setReprintNote(e instanceof Error ? e.message : 'Could not queue the reprint.');
        beep('bad');
      }
    } finally {
      reissuingRef.current = false;
    }
  }

  /**
   * Undo = dismiss the row this report created. This is the one UNREVIEWED dismiss in the system —
   * one tap, a canned reason, no sight of who else reported the garment — so it has to earn that.
   *
   * ⚠️ `merged` from the add response is only "merged AS OF the add". Nothing refreshes it, and the
   * panel lives until the next scan, so a cutter reporting the same garment a minute later is
   * invisible to it: `rev` moves but `entryId` does not, and the server's delete conditions on
   * `entryId` alone. The one-tap Undo would then destroy the cutter's defect report too, and the
   * order would stop being blocked with the shirt still ruined. So re-read and refuse if the row
   * moved at all — `rev` is bumped ONLY by an add, so `rev !== revSeen` means exactly "reported
   * again". The queue page's dismiss stays available for that case; it shows the reporting stations
   * and takes a typed reason, which is the review this one skips.
   */
  async function removeQueueEntry() {
    if (reprint.phase !== 'queued' || reprint.merged) return;
    if (!result) return;
    const { entryId, merged, transferOnly, rev } = reprint;
    // Captured BEFORE the awaits: every outcome below is painted only if the operator is still
    // looking at this garment. Without it a failed Undo re-paints "✓ Added to the reprint queue"
    // over whatever was scanned next, and wires that panel's Undo to this garment's row.
    const { code, storeId, orderId } = result;
    setReprint({ phase: 'removing', entryId, merged, transferOnly, rev });
    setReprintNote(null);
    try {
      const list = await client.GET('/batches/reprint-cart/entries');
      if (list.error || !list.data?.ok) {
        throw new Error(list.error?.error?.detail ?? 'Could not check the reprint queue.');
      }
      const row = list.data.data.entries.find((e) => e.entryId === entryId);
      // A row that is gone needs no undo; let the DELETE below answer for it (404 / already flushed)
      // rather than guessing here.
      if (row && row.rev !== rev) {
        throw new Error(
          'Another station has reported this garment since — removing it here would delete their report too. Use the reprint queue page, where you can see who reported it.',
        );
      }
      const res = await client.DELETE('/batches/reprint-cart/entries/{entryId}', {
        params: { path: { entryId } },
        body: { reason: 'Undone at the heat press immediately after reporting' },
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Could not undo that.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      // Unconditional: the row is gone whether or not the operator has scanned on.
      if (storeId && orderId) refreshAfterQueueChange(storeId, orderId);
      if (isCurrent(code)) {
        setReprint({ phase: 'idle' });
        setReprintNote('Removed from the reprint queue.');
        beep('ok');
      }
    } catch (e) {
      if (isCurrent(code)) {
        // Stay in `queued` so Undo remains available — and so the panel never implies the row is gone.
        setReprint({ phase: 'queued', entryId, merged, transferOnly, rev });
        setReprintNote(e instanceof Error ? e.message : 'Could not undo that.');
        beep('bad');
      }
    }
  }

  /**
   * "Print now" — owner decision D4's other half: a one-entry flush of the row this station just
   * created, for the garment that cannot wait for the day's merged sheet.
   *
   * Two steps, because it spends film and a blank. The first call omits `confirmFlush` and the server
   * answers 422 `reprint_flush_confirmation_required` carrying the real cost; that is a PRICE, not a
   * failure, and is rendered as a confirm. The second call carries `confirmFlush: true`.
   *
   * Three things here are load-bearing:
   *
   *  · It sends the `{entryId, rev}` THE ADD RETURNED, never a re-read. If another station reported
   *    the same garment in between, `rev` has moved and the server refuses the whole flush — which is
   *    correct, because that report may have flipped `transferOnly` and therefore the bill.
   *  · It NEVER re-adds on failure. The row already exists; a retry that also re-added would bump
   *    `rev` for no reason, and on an already-flushed row that costs a second sheet and a second blank.
   *  · A 200 is not proof a sheet exists. The flush converges on a retry (`deduplicated: true`) and
   *    can legitimately come back with an EMPTY `sheets` array when the row it converged onto points
   *    at a batch that is gone. Claiming "printing" there would send the operator looking for a film
   *    nobody made, so an empty result is treated as a refusal.
   */
  async function printNow(confirmed: boolean) {
    // The same guard the add uses: this is a money action, and a second one in flight is a second film.
    if (reissuingRef.current) return;
    const from = reprint;
    if (from.phase !== 'queued' && from.phase !== 'quoted' && from.phase !== 'flushFailed') return;
    if (!result) return;
    const row: QueuedRow = {
      entryId: from.entryId,
      merged: from.merged,
      transferOnly: from.transferOnly,
      rev: from.rev,
    };
    const { code, storeId, orderId } = result;
    reissuingRef.current = true;
    setReprint({ phase: confirmed ? 'printing' : 'quoting', ...row });
    setReprintNote(null);
    try {
      const res = await client.POST('/batches/reprint-cart/flush', {
        body: {
          entries: [{ entryId: row.entryId, rev: row.rev }],
          ...(confirmed ? { confirmFlush: true } : {}),
        },
      });
      if (res.error) {
        const body = res.error.error as Record<string, unknown> | undefined;
        if (body?.type === 'reprint_flush_confirmation_required') {
          if (isCurrent(code)) {
            setReprint({
              phase: 'quoted',
              ...row,
              cost: {
                garments: Number(body.garments ?? 0),
                replacementBlanks: Number(body.replacementBlanks ?? 0),
                sheets: Number(body.sheets ?? 0),
              },
            });
          }
          return;
        }
        // Everything else — blocked, stale, raced, gone, forbidden — is the server's sentence to
        // deliver verbatim. It is the only copy that names what to actually do.
        throw new Error(String(body?.detail ?? 'Could not print it.'));
      }
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      const out = res.data.data;
      const sheet = out.sheets[0];
      if (!sheet) {
        throw new Error(
          'the server reported no sheet. Check the reprint queue before printing again — do not retry blind.',
        );
      }
      // ⚠️ A 200 does NOT mean this call built that sheet. The route converges: a row already carrying
      // `flushedIntoBatchId` is skipped ALREADY_FLUSHED, and once every named row is skipped it answers
      // 200 `deduplicated: true` with the sheet those rows are ALREADY on — before the `confirmFlush`
      // price gate, so even the unconfirmed quote tap gets it. That sheet is very often the office's
      // whole-queue flush carrying a dozen other garments. Treating it as ours would put a one-tap
      // "discard the sheet" behind it, and deleting a READY sheet reopens every line on it,
      // reverse-consumes their blanks, and PERMANENTLY retires the rows of any garment already
      // pressed — while the film that was really printed becomes untracked. So: only a sheet this
      // call created, holding exactly this one row, may carry the inline discard.
      const isOurs =
        out.deduplicated === false &&
        sheet.entryIds.length === 1 &&
        sheet.entryIds[0] === row.entryId;
      if (!isOurs) {
        if (isCurrent(code)) {
          setReprint({ phase: 'onSheet', ...row, seq: sheet.batch.seq });
          beep('ok');
        }
        return;
      }
      // A sheet now exists, so the batch list moved as well as the queue — unlike a bare add.
      if (storeId && orderId) refreshAfterQueueChange(storeId, orderId);
      qc.invalidateQueries({ queryKey: ['batches'] });
      if (isCurrent(code)) {
        setReprint({
          phase: 'done',
          batchId: sheet.batch.batchId,
          seq: sheet.batch.seq,
          fromQueue: true,
          row,
        });
        beep('ok');
      }
    } catch (e) {
      if (isCurrent(code)) {
        // Back to a state that says "not printed" without implying the row is gone — it isn't.
        setReprint({
          phase: 'flushFailed',
          ...row,
          problem: e instanceof Error ? e.message : 'the flush was refused.',
        });
        beep('bad');
      }
    } finally {
      reissuingRef.current = false;
    }
  }

  // ── Defect → immediate reissue (Rollo Phase 5) — the NOT-OWED path only ─────────────────────────
  // Kept off the queue deliberately: see `reprintDestination`. A goodwill reprint carries an override
  // that the queue record has no field for and the flush's owed gate would refuse forever, so this
  // one still mints its own sheet. The reissue draws no blank until ITS OWN mark-printed, so the undo
  // is purely "delete the reissue batch".
  async function confirmReprint(overrideReason?: ReissueNotOwedReason) {
    // Re-entrancy guard (survives a rescan-reset): never mint a second reprint while one is in flight.
    if (reissuingRef.current) return;
    if (!result?.batchId) return;
    reissueTokenRef.current = reissueTokenFor(reissueTokenRef.current, result.code, () =>
      crypto.randomUUID(),
    );
    const body = reissueBodyFor(result, {
      ...(reissueTokenRef.current.token ? { clientToken: reissueTokenRef.current.token } : {}),
      ...(overrideReason ? { override: overrideReason } : {}),
    });
    if (!body) return;
    const { batchId, code } = { batchId: result.batchId, code: result.code };
    reissuingRef.current = true;
    setReprint({ phase: 'pending' });
    setReprintNote(null);
    try {
      const res = await client.POST('/batches/{batchId}/reissue', {
        params: { path: { batchId } },
        body,
      });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Could not queue the reprint.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      // The reissue batch exists now whether or not the operator is still on this garment (the
      // comment below says as much), so the gang-sheets list must learn about it either way.
      refreshAfterBatchChange(body.garments[0]?.storeId, body.garments[0]?.orderId);
      // Retire the token on success, OUTSIDE the isCurrent guard below. Inside it, a mid-flight rescan
      // would leave the token live and the NEXT garment's reprint would replay THIS batch as a 200 —
      // ship short, with a "queued" confirmation. Never cleared on failure: that is what makes the
      // retry-after-502 dedupe.
      reissueTokenRef.current = { token: null, code: null };
      // Only land the "done" panel if the operator is still on this garment (else they scanned on — the
      // reprint IS queued and shows as outstanding via Phase 4, they just lose the quick inline undo).
      if (isCurrent(code)) {
        setReprint({ phase: 'done', batchId: res.data.data.batchId, seq: res.data.data.seq });
        beep('ok');
      }
    } catch (e) {
      if (isCurrent(code)) {
        setReprint({ phase: 'idle' });
        setReprintNote(e instanceof Error ? e.message : 'Reprint failed.');
        beep('bad');
      }
    } finally {
      reissuingRef.current = false;
    }
  }

  // Undo = delete the just-created reissue batch (scope-keyed to THIS batchId). Safe: a not-yet-printed
  // reissue owns no source line and drew no blank, so delete reopens nothing and reverses nothing. A
  // freshly-created reissue is briefly COMPOSITING (not deletable → 409); we stay in "done" so Undo can
  // be retried once it reaches READY.
  async function undoReprint() {
    if (reprint.phase !== 'done') return;
    const { batchId, seq, fromQueue, row } = reprint;
    // Captured before the await — `result` moves the moment the operator scans on, so reading it
    // afterwards would invalidate the NEXT garment's order and leave this one's cache stale. The
    // `isCurrent` guards below are the same fix the queue Undo needs, applied here too: this handler
    // predates P6 and had neither, so a failed discard could paint "✓ Reprint queued — Sheet #N"
    // over a garment that has no reprint at all.
    const { code, storeId, orderId } = result ?? {};
    setReprint({ phase: 'undoing', batchId, seq, ...(fromQueue ? { fromQueue, row } : {}) });
    setReprintNote(null);
    try {
      const res = await client.DELETE('/batches/{batchId}', { params: { path: { batchId } } });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Could not discard the reprint.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      refreshAfterBatchChange(storeId, orderId);
      if (fromQueue) refreshAfterQueueChange(storeId ?? '', orderId ?? '');
      if (code == null || isCurrent(code)) {
        // Two different things can have just happened, and the operator has to know which.
        // Discarding a flushed QUEUE row's sheet un-flushes the row: the garment is back in the
        // queue, STILL RUINED and still needing a reprint. Landing on `idle` there would both lie by
        // omission and re-arm "Mark defective" over a garment that is already queued. Discarding the
        // not-owed reissue really does drop the reprint, so `idle` is right for that one.
        // (`unflush` clears the flush marks without bumping `rev`, so the pinned row is still live.)
        if (fromQueue && row) {
          setReprint({ phase: 'queued', ...row });
          setReprintNote('Sheet discarded — the garment is back in the reprint queue.');
        } else {
          setReprint({ phase: 'idle' });
          setReprintNote('Reprint discarded.');
        }
        beep('ok');
      }
    } catch (e) {
      if (code == null || isCurrent(code)) {
        // Stay in "done" so Undo remains available (e.g. a compositing 409 → retry in a moment).
        setReprint({ phase: 'done', batchId, seq, ...(fromQueue ? { fromQueue, row } : {}) });
        setReprintNote(
          e instanceof Error ? e.message : 'Could not discard the reprint — try again in a moment.',
        );
        beep('bad');
      }
    }
  }

  return (
    <div className="dark flex h-dvh w-full flex-col overflow-hidden bg-background text-foreground">
      {/* Top chrome bar */}
      <header className="flex flex-none items-center justify-between gap-3 border-b px-5 py-2.5">
        <div className="flex items-center gap-3">
          <img src="/fsp-logo.png" alt="Fast Screen Print" className="h-10 w-auto" />
          <div className="leading-tight">
            <div className="font-semibold">Scan station</div>
            <div className="text-xs text-muted-foreground">Heat press</div>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <span
            className={cn(
              'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium',
              manualOpen
                ? 'border-border text-muted-foreground'
                : 'border-emerald-800 bg-emerald-950/50 text-emerald-200',
            )}
          >
            <span
              className={cn(
                'size-2 rounded-full',
                manualOpen ? 'bg-muted-foreground' : 'animate-pulse bg-emerald-400',
              )}
            />
            {manualOpen ? 'Manual entry' : 'Scanner ready'}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setManualOpen((v) => !v)}
            className="text-muted-foreground"
          >
            <Keyboard className="size-4" /> Type a code
          </Button>
          <LayoutSwitch
            layout={prefs.layout}
            onChange={(layout) => setPrefs({ ...prefs, layout })}
          />
          <span
            className="hidden items-center rounded-full border px-3 py-1.5 text-sm text-muted-foreground md:inline-flex"
            title={stationEmail}
          >
            Station account
          </span>
          <Link
            to="/orders"
            className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            Dashboard
          </Link>
        </div>
      </header>

      {/* Manual-entry bar (fallback for a torn label). Capture is paused while this is open. */}
      {manualOpen && (
        <div className="flex flex-none items-center gap-2 border-b bg-muted/30 px-5 py-3">
          <Input
            ref={manualRef}
            autoFocus
            inputMode="text"
            placeholder="Type the code from the label…"
            className="h-12 max-w-md text-lg"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submitManual();
              } else if (e.key === 'Escape') {
                setManualOpen(false);
              }
            }}
          />
          <Button type="button" onClick={submitManual} className="h-12 text-base">
            <ScanLine className="size-5" /> Look up
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setManualOpen(false)}
            className="h-12"
            aria-label="Cancel manual entry"
          >
            <X className="size-5" />
          </Button>
        </div>
      )}

      {/* Body */}
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {errorMsg && (
          <div className="flex-none border-b border-red-800 bg-red-950/60 px-5 py-2 text-sm text-red-100">
            {errorMsg}
          </div>
        )}

        {loading ? (
          <div className="flex flex-1 items-center justify-center gap-3 text-lg text-muted-foreground">
            <Loader2 className="size-6 animate-spin" /> Resolving…
          </div>
        ) : result ? (
          <ScanResultView
            result={result}
            styleCodeByDesignId={styleCodeByDesignId}
            pending={pending}
            onToggle={toggle}
            press={
              prefs.layout === 'press'
                ? {
                    prefs,
                    onPrefsChange: setPrefs,
                    sideIndex,
                    onSideChange: (i) => {
                      setSideIndex(i);
                      // A deliberate tap is the operator's intent for the run: the next scan opens here.
                      stickyLocationRef.current = orderedPlacements[i]?.location ?? null;
                    },
                    onPickerOpenChange: setPickerOpen,
                    barDisarmed,
                  }
                : undefined
            }
            shelfSlot={
              // Only a garment DONE with production (batch closed) or NO LONGER OWED (refund landed
              // after press) belongs on the finished shelf — an OK garment is owed to its order and
              // gets pressed, not shelved. Keyed by code so a new scan resets the panel's state.
              result.status === 'ALREADY_PRINTED' || result.status === 'DO_NOT_PRESS' ? (
                <AddToShelfPanel key={result.code} code={result.code} />
              ) : undefined
            }
            reprintSlot={
              reprintDestination(result) !== 'hidden' ? (
                <ReprintPanel
                  mode={reprintDestination(result) === 'reissue' ? 'reissue' : 'queue'}
                  state={reprint}
                  note={reprintNote}
                  crossRunNote={reprintCrossRun}
                  onStart={() => {
                    setReprintNote(null);
                    setReprint({ phase: 'confirming' });
                  }}
                  onConfirmQueue={confirmQueueAdd}
                  onConfirmReissue={confirmReprint}
                  onCancel={() => {
                    // From the cost quote, Cancel means "keep it in the queue" — NOT "forget the
                    // report". Dropping to idle here would re-arm "Mark defective" over a garment
                    // that is already queued, and a second report bumps `rev` for nothing.
                    setReprintNote(null);
                    setReprint((prev) =>
                      prev.phase === 'quoted' || prev.phase === 'flushFailed'
                        ? {
                            phase: 'queued',
                            entryId: prev.entryId,
                            merged: prev.merged,
                            transferOnly: prev.transferOnly,
                            rev: prev.rev,
                          }
                        : { phase: 'idle' },
                    );
                  }}
                  onRemove={removeQueueEntry}
                  onPrintNow={printNow}
                  onUndo={undoReprint}
                />
              ) : null
            }
          />
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center gap-6 text-center">
            <div className="flex size-40 items-center justify-center rounded-3xl border-2 border-dashed border-muted-foreground/40">
              <ScanLine className="size-16 text-muted-foreground" />
            </div>
            <div>
              <div className="text-3xl font-extrabold">Scan a garment</div>
              <div className="mt-1 text-lg text-muted-foreground">
                Point the handheld at the QR on the inside hem
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
